import { formatNumber } from './number.ts';
import type { Digits } from './digits.ts';
import type { Locale } from '../locale.ts';

export type Toman = number; // integer Toman (API uses bigint; JS number is safe up to 9e15 Toman)

const UNIT = { fa: 'تومان', en: 'Toman' } as const;

/** "۱٬۲۵۰٬۰۰۰ تومان" | "1,250,000 Toman" */
export function formatToman(amount: Toman, locale: Locale, digits: Digits, opts: { unit?: boolean } = {}): string {
  const n = formatNumber(Math.round(amount), locale, digits, { maximumFractionDigits: 0 });
  return opts.unit === false ? n : `${n} ${UNIT[locale]}`;
}

/** Compact: "۱٫۲۵ میلیون تومان" | "1.25M Toman"; below 1000 falls back to full. */
export function formatTomanCompact(amount: Toman, locale: Locale, digits: Digits, opts: { unit?: boolean } = {}): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  const unit = opts.unit === false ? '' : ` ${UNIT[locale]}`;
  const scale = (v: number, faWord: string, enSuffix: string) => {
    const digitsMax = v >= 100 ? 0 : v >= 10 ? 1 : 2;
    const num = formatNumber(v, locale, digits, { maximumFractionDigits: digitsMax });
    return locale === 'fa' ? `${sign}${num} ${faWord}${unit}` : `${sign}${num}${enSuffix}${unit}`;
  };
  if (abs >= 1_000_000_000) return scale(abs / 1_000_000_000, 'میلیارد', 'B');
  if (abs >= 1_000_000) return scale(abs / 1_000_000, 'میلیون', 'M');
  if (abs >= 1_000) return scale(abs / 1_000, 'هزار', 'K');
  return formatToman(amount, locale, digits, opts);
}

/** Parse user-typed Toman ("۱٬۲۵۰٬۰۰۰", "1,250,000", "1.25m") → integer Toman or null. */
export function parseToman(input: string): Toman | null {
  const s = input
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٬,\s_]/g, '')
    .replace(/٫/g, '.')
    .trim()
    .toLowerCase();
  const m = s.match(/^(-?\d+(?:\.\d+)?)\s*(m|k|b|میلیون|هزار|میلیارد)?$/);
  if (!m) return null;
  const base = parseFloat(m[1]!);
  const mult = { m: 1e6, میلیون: 1e6, k: 1e3, هزار: 1e3, b: 1e9, میلیارد: 1e9 }[m[2] ?? ''] ?? 1;
  return Math.round(base * mult);
}
